import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { asyncScheduler, observeOn, startWith, take } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { SidebarComponent } from '@core/common-smart-ui';
import { TranslatePipe } from '@core/i18n-legacy';
import { ButtonComponent, DividerComponent, HeadingComponent } from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type { DataSource, View, ViewForm } from '@core/views-types';
import { ViewNameInputComponent } from '@core/views-ui';
import { dataSource$ } from '@core/views-util';
import { VisibilitySelectorContainerComponent } from '@core/visibility-legacy';
import type { Visibility } from '@core/visibility-types';

import { ViewsAnalytics } from '../../../analytics/views-analytics';
import { injectViewsVisibilityLabels, VisibilityPrivate } from '../../consts';
import { ViewFormService } from '../../view-form.service';
import { ViewLayoutPickerComponent } from '../../view-layout-picker/view-layout-picker.component';
import { ViewPageService } from '../view-page.service';

@Component({
  selector: 'rt-views-create-view-sidebar',
  imports: [
    HeadingComponent,
    DividerComponent,
    ViewNameInputComponent,
    SidebarComponent,
    TranslatePipe,
    ViewLayoutPickerComponent,
    ButtonComponent,
    ReactiveFormsModule,
    VisibilitySelectorContainerComponent,
  ],
  templateUrl: './create-view-sidebar.component.html',
  styleUrls: ['./create-view-sidebar.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateViewSidebarComponent {
  private readonly viewFormService = inject(ViewFormService);

  public readonly visibilityLabels = injectViewsVisibilityLabels();
  public viewForm = this.viewFormService.viewForm;
  public dataSource: DataSource;

  private dataSource$ = inject(dataSource$);
  private cdRef = inject(ChangeDetectorRef);
  private untilDestroyed = injectUntilDestroyed();
  private viewPageService = inject(ViewPageService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private viewsAnalytics = inject(ViewsAnalytics);
  private viewFormSnapshot: ViewForm['value'];
  private currentView: View | null = null;

  public ngOnInit() {
    this.dataSource$.pipe(this.untilDestroyed).subscribe(dataSource => {
      this.dataSource = dataSource;
    });

    this.viewPageService.currentView$
      .pipe(filterBoolean, observeOn(asyncScheduler), take(1), this.untilDestroyed)
      .subscribe(currentView => {
        this.viewFormSnapshot = this.viewForm.value;
        this.currentView = currentView;
        if (currentView.name === this.viewForm.value.name) {
          const name = this.viewPageService.nameToDuplicate(currentView.name);
          this.viewForm.controls.name.patchValue(name);
        }

        if (currentView.codename) {
          this.viewForm.controls.symbol.patchValue(null);
        }

        if (!!currentView.codename && this.viewForm.controls.visibility.pristine) {
          this.viewForm.controls.visibility.patchValue(VisibilityPrivate);
        }
      });

    this.viewForm.valueChanges
      .pipe(startWith(this.viewForm.value), this.untilDestroyed)
      .subscribe(() => this.cdRef.detectChanges());
  }

  public ngOnDestroy() {
    if (this.viewFormSnapshot) {
      const { name, symbol, visibility } = this.viewFormSnapshot;
      this.viewFormService.viewForm.patchValue({ name, symbol, visibility });
    }
  }

  public onVisibilityChanges(visibility: Visibility) {
    this.viewForm.controls.visibility.setValue(visibility);
  }

  public cancel() {
    this.router.navigate(['..'], { relativeTo: this.activatedRoute });
  }

  public createView() {
    this.viewPageService.create();
    this.viewsAnalytics.created({ fromCodename: this.currentView?.codename });
    this.cdRef.detectChanges();
  }
}
